import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import app from '../app';
import highlight from '../../common/helpers/highlight';
import extractText from '../../common/utils/extractText';
import Link from '../../common/components/Link';
import Icon from '../../common/components/Icon';
import PermissionGrid from './PermissionGrid';
import escapeRegExp from '../../common/utils/escapeRegExp';
import ItemList from '../../common/utils/ItemList';
export class GeneralSearchResult {
  constructor(id, category, icon, tree, link, help) {
    this.id = id;
    this.category = category;
    this.icon = icon;
    this.tree = tree;
    this.link = link;
    this.help = help;
  }
}

/**
 * Finds and displays settings, permissions and installed extensions (i.e. general search results) in the search dropdown.
 */
export default class GeneralSearchSource {
  constructor() {
    _defineProperty(this, "results", new Map());
    _defineProperty(this, "resource", 'general');
  }
  title() {
    return extractText(app.translator.trans('core.admin.search_source.general.heading'));
  }
  isCached(query) {
    return this.results.has(query.toLowerCase());
  }
  async search(query, limit) {
    query = query.toLowerCase();
    return new Promise(resolve => {
      const results = [];

      // extensions.
      for (const extensionId in app.data.extensions) {
        const extension = app.data.extensions[extensionId];
        const title = extension.extra['flarum-extension'].title || extensionId.replace('core-', '');
        const icon = extension.icon || {
          name: 'fas fa-cog'
        };
        const category = extension.extra['flarum-extension'].category || 'other';
        if (this.itemHasQuery(title, query)) {
          results.push(new GeneralSearchResult(extensionId, app.translator.trans('core.admin.nav.categories.' + category, {}, true), icon, [title], app.route('extension', {
            id: extensionId
          })));
        }
      }

      // extension registered settings && permissions
      results.push(...this.lookup(app.registry.getData(), query));

      // manually registered settings && permissions into the search index.
      results.push(...this.lookup(app.generalIndex.getData(), query));
      this.results.set(query, results);
      m.redraw();
      resolve();
    });
  }
  lookup(data, query) {
    const extensions = app.data.extensions;
    const permissionItems = PermissionGrid.prototype.permissionItems();
    const results = [];
    for (const extensionId in data) {
      // settings
      const settings = data[extensionId].settings;
      let normalizedSettings = [];
      if (settings instanceof ItemList) {
        normalizedSettings = settings == null ? void 0 : settings.toArray();
      } else if (settings) {
        normalizedSettings = settings;
      }
      for (const setting of normalizedSettings) {
        if ('visible' in setting && !setting.visible()) {
          continue;
        }
        const label = 'label' in setting ? extractText(setting.label) : '';
        const help = 'help' in setting ? extractText(setting.help) : '';
        const corePage = !extensions[extensionId] ? extensionId.replace('core-', '') : null;
        const group = app.generalIndex.getGroup(extensionId);
        if (this.itemHasQuery(label, query) || this.itemHasQuery(help, query)) {
          var _extensions$extension, _extensions$extension2;
          const id = extensionId + '-' + ('setting' in setting ? setting : 'id' in setting ? setting.id : '');
          results.push(new GeneralSearchResult(id, (group == null ? void 0 : group.label) || ((_extensions$extension = extensions[extensionId]) == null ? void 0 : _extensions$extension.extra['flarum-extension'].title) || app.translator.trans('core.admin.' + corePage + '.title', {}, true), (group == null ? void 0 : group.icon) || ((_extensions$extension2 = extensions[extensionId]) == null ? void 0 : _extensions$extension2.icon) || {
            name: 'fas fa-cog'
          }, 'tree' in setting && setting.tree ? [...setting.tree.map(extractText), label] : [label], (group == null ? void 0 : group.link) || (corePage ? app.route(corePage) : app.route('extension', {
            id: extensionId
          })), help));
        }
      }

      // permissions
      const permissions = data[extensionId].permissions || {};
      for (const permissionType in permissions) {
        // @ts-ignore
        const permissionList = (permissions[permissionType] || []).toArray();
        for (const permission of permissionList) {
          const label = extractText(permission.label);
          if (this.itemHasQuery(label, query)) {
            var _extensions$extension3, _extensions$extension4, _permissionItems$get;
            const id = extensionId + '-' + permissionType + '-' + permission.permission;
            const corePage = !extensions[extensionId] ? extensionId.replace('core-', '') : null;
            const group = app.generalIndex.getGroup(extensionId);
            results.push(new GeneralSearchResult(id, (group == null ? void 0 : group.label) || ((_extensions$extension3 = extensions[extensionId]) == null ? void 0 : _extensions$extension3.extra['flarum-extension'].title) || app.translator.trans('core.admin.' + corePage + '.title', {}, true), (group == null ? void 0 : group.icon) || ((_extensions$extension4 = extensions[extensionId]) == null ? void 0 : _extensions$extension4.icon) || {
              name: 'fas fa-key'
            }, [app.translator.trans('core.admin.permissions.title', {}, true), extractText((_permissionItems$get = permissionItems.get(permissionType)) == null ? void 0 : _permissionItems$get.label) || permissionType, label], (group == null ? void 0 : group.link) || (corePage ? app.route(corePage) : app.route('extension', {
              id: extensionId
            }))));
          }
        }
      }
    }
    return results;
  }
  itemHasQuery(item, query) {
    return query.split(' ').every(part => item.toLowerCase().includes(part));
  }
  view(query) {
    const results = (this.results.get(query) || []).slice(0, 30);
    const categories = Array.from(new Set([...results.map(r => r.category)]));
    if (!categories.length) return [];
    return categories.map(category => {
      return m('[', null, m("li", {
        className: "GeneralSearchResult-group Dropdown-header"
      }, category), results.filter(r => r.category === category).map(result => {
        const phrase = escapeRegExp(query);
        const highlightRegExp = new RegExp(phrase + '|' + phrase.trim().replace(/\s+/g, '|'), 'gi');
        const tree = result.tree.map(part => {
          return highlight(part, highlightRegExp, undefined, true);
        });
        let help = result.help;
        if (help) {
          help = highlight(help, highlightRegExp, 100, true);
        }
        return m("li", {
          className: "GeneralSearchResult",
          "data-index": 'general-' + result.id,
          "data-id": result.id
        }, m(Link, {
          href: result.link
        }, m("div", {
          className: "ExtensionIcon",
          style: result.icon
        }, m(Icon, {
          name: result.icon.name
        })), m("div", {
          className: "GeneralSearchResult-info"
        }, m("div", {
          className: "GeneralSearchResult-tree"
        }, tree.map((part, i) => {
          return [m("span", null, part), i < tree.length - 1 ? m("span", {
            className: "GeneralSearchResult-tree-separator"
          }, m(Icon, {
            name: "fas fa-arrow-right"
          })) : null];
        })), help ? m("div", {
          className: "GeneralSearchResult-help"
        }, help) : null)));
      }));
    });
  }
  customGrouping() {
    return true;
  }
  fullPage(query) {
    return null;
  }
  gotoItem(id) {
    return null;
  }
}
flarum.reg.add('core', 'admin/components/GeneralSearchSource', GeneralSearchSource);